import { cn } from "@au-schein-de/au-ui-library";
import type { EmblaCarouselType } from "embla-carousel";
import { useCallback, useEffect, useRef, useState } from "react";

import { getCarouselThumbScrollRatio } from "./get-carousel-thumb-scroll-ratio";

interface ExclusiveProductsCarouselScrollBarProps {
	api: EmblaCarouselType | undefined;
	controlsId: string;
	className?: string;
}

const THUMB_MIN_WIDTH_RATIO = 0.27;

export function ExclusiveProductsCarouselScrollBar({
	api,
	controlsId,
	className,
}: ExclusiveProductsCarouselScrollBarProps) {
	const trackRef = useRef<HTMLDivElement>(null);
	const [scrollRatio, setScrollRatio] = useState(0);
	const [thumbWidthRatio, setThumbWidthRatio] = useState(THUMB_MIN_WIDTH_RATIO);
	const updateScrollState = useCallback(() => {
		if (!api) {
			return;
		}
		const snapCount = api.scrollSnapList().length;
		const selectedIndex = api.selectedScrollSnap();
		setScrollRatio(getCarouselThumbScrollRatio(selectedIndex, snapCount));
		setThumbWidthRatio(
			snapCount <= 1 ? 1 : Math.max(THUMB_MIN_WIDTH_RATIO, 1 / snapCount),
		);
	}, [api]);
	useEffect(() => {
		if (!api) {
			return;
		}
		updateScrollState();
		api.on("scroll", updateScrollState);
		api.on("reInit", updateScrollState);
		api.on("select", updateScrollState);
		return () => {
			api.off("scroll", updateScrollState);
			api.off("reInit", updateScrollState);
			api.off("select", updateScrollState);
		};
	}, [api, updateScrollState]);
	const scrollToRatio = useCallback(
		(ratio: number) => {
			if (!api) {
				return;
			}
			const snapCount = api.scrollSnapList().length;
			if (snapCount <= 1) {
				return;
			}
			const clampedRatio = Math.max(0, Math.min(1, ratio));
			const targetIndex = Math.round(clampedRatio * (snapCount - 1));
			api.scrollTo(targetIndex);
		},
		[api],
	);
	const handleTrackPointer = useCallback(
		(clientX: number) => {
			const track = trackRef.current;
			if (!track || !api) {
				return;
			}
			const rect = track.getBoundingClientRect();
			const ratio = (clientX - rect.left) / rect.width;
			scrollToRatio(ratio);
		},
		[api, scrollToRatio],
	);
	const thumbOffsetRatio = scrollRatio * Math.max(0, 1 - thumbWidthRatio);
	if (!api || api.scrollSnapList().length <= 1) {
		return null;
	}
	return (
		<div
			ref={trackRef}
			role="scrollbar"
			aria-controls={controlsId}
			aria-orientation="horizontal"
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={Math.round(scrollRatio * 100)}
			className={cn(
				"relative h-1.5 w-full cursor-pointer rounded-[5px] bg-gray-150 p-px",
				className,
			)}
			onClick={(event) => {
				if (event.target === trackRef.current) {
					handleTrackPointer(event.clientX);
				}
			}}
			onKeyDown={(event) => {
				if (event.key === "ArrowRight") {
					api.scrollNext();
				}
				if (event.key === "ArrowLeft") {
					api.scrollPrev();
				}
			}}
			tabIndex={0}
			data-testid="exclusive-products-carousel-scrollbar"
		>
			<div className="pointer-events-none absolute inset-x-px inset-y-px">
				<div
					className="pointer-events-auto absolute h-1 rounded-[6px] bg-gray-350"
					style={{
						width: `${thumbWidthRatio * 100}%`,
						left: `${thumbOffsetRatio * 100}%`,
					}}
					onPointerDown={(event) => {
						event.preventDefault();
						const track = trackRef.current;
						if (!track) {
							return;
						}
						const onPointerMove = (moveEvent: PointerEvent) => {
							handleTrackPointer(moveEvent.clientX);
						};
						const onPointerUp = () => {
							track.removeEventListener("pointermove", onPointerMove);
							track.removeEventListener("pointerup", onPointerUp);
						};
						track.setPointerCapture(event.pointerId);
						track.addEventListener("pointermove", onPointerMove);
						track.addEventListener("pointerup", onPointerUp);
					}}
				/>
			</div>
		</div>
	);
}
